// The code in the sign-in email: what counts as one, and when "send it again"
// may be offered. Pure (no React, no I/O) so the rules are pinned by a test and
// EmailCodeEntry stays a thin shell around them.
//
// THE LENGTH IS SUPABASE'S, NOT OURS (2026-10-22). The auth hook mails the token
// Supabase hands it, verbatim, and the project's "email OTP length" can be
// anything from 6 to 10. This file used to say "always six"; the project was
// issuing eight, the box took six and cut a paste to its first six, and every
// code anyone typed came back "expired or invalid" (Nischay, 2026-10-20: two
// failed codes, then the link). So the box takes any length Supabase can send.

/** The shortest and longest code Supabase can be configured to send. */
export const CODE_MIN = 6
export const CODE_MAX = 10

/** How long the person waits before "send it again" appears. Long enough for the
 *  mail to have arrived if it was going to, short enough that nobody sits on a
 *  dead end. (The auth server also holds its own cooldown between sends, which
 *  friendlyAuthError words kindly if it is still running.) */
export const RESEND_AFTER_MS = 60_000

/** A typed code is checked once the typing stops this long (it can't be checked
 *  at a fixed digit, because the length is not ours to know). A paste or the
 *  OS autofill is checked at once. */
export const TYPED_SETTLE_MS = 1_100

/** one code: 6–10 digits, maybe grouped by spaces or dashes ("1234 5678") */
const CODE_RUN = new RegExp(`\\d(?:[ \\-\\u2010-\\u2014]?\\d){${CODE_MIN - 1},${CODE_MAX - 1}}`)

/** Whatever was typed or pasted, reduced to the code in it:
 *  "123 456", "1234-5678", " 123456\n" and "Your code is 12345678" all become
 *  their digits. A sentence with other numbers in it keeps the CODE, not every
 *  digit ("your code is 12345678, it expires in 10 minutes" → "12345678").
 *  Full-width digits (some phone keyboards type them) count as digits. */
export function codeDigits(raw: string | null | undefined): string {
  const s = (raw ?? '').normalize('NFKC')
  const run = s.match(CODE_RUN)
  const digits = (run ? run[0] : s).replace(/\D/g, '')
  return digits.slice(0, CODE_MAX)
}

/** Is this a whole code, ready to send for checking? */
export function isWholeCode(digits: string): boolean {
  return /^\d+$/.test(digits) && digits.length >= CODE_MIN && digits.length <= CODE_MAX
}
